import { BaseComponent } from '../../components/base-component'
import { isLeftKey, isRightKey } from '../../utils/key'
import { $el, transitionToPromise } from 'src/scripts/utils/utils'

const BUTTON_SELECTOR = '[data-before-after-images-button]'
const STEP = 20
const WITH_TRANSITION_CLASS = 'before-after--with-transition'
const SEPARATOR_SELECTOR = '[data-before-after-images-separator]'

export class BeforeAfterImages extends BaseComponent {
  isMoving: boolean = false
  intersectionObserver: IntersectionObserver

  mountComponent(): void {
    const button = $el(BUTTON_SELECTOR, this)

    this.addListener(button, 'keyup', this.handleButtonKeyUp)
    this.addListener(button, 'mousedown', this.handleButtonMouseDown)
    this.addListener(document, 'mouseup', this.handleButtonMouseUp)
    this.addListener(document, 'mousemove', this.handleMouseMove)
    this.addListener(button, 'touchstart', this.handleTouchStart)
    this.addListener(this, 'touchmove', this.handleTouchMove)
    this.addListener(document, 'touchend', this.handleTouchEnd)

    this.intersectionObserver = new IntersectionObserver(
      this.handleInViewport,
      { rootMargin: `0px` },
    )

    this.intersectionObserver.observe(this)
  }

  unmountComponent(): void {
    this.intersectionObserver?.disconnect()
  }

  setInitialPosition = async () => {
    const separator = $el(SEPARATOR_SELECTOR, this)

    this.setPosition(Number(this.dataset.initialDragPosition))
    await transitionToPromise(separator)
    this.classList.remove(WITH_TRANSITION_CLASS)
  }

  handleInViewport = (entries: IntersectionObserverEntry[]): void => {
    if (entries.some(entry => entry.isIntersecting)) {
      this.intersectionObserver.disconnect()
      this.setInitialPosition()
    }
  }

  handleButtonMouseDown = (event: MouseEvent): void => {
    event.preventDefault()
    this.startMove()
  }

  handleButtonMouseUp = (): void => {
    this.endMove()
  }

  handleMouseMove = (event: MouseEvent): void => {
    if (!this.isMoving) {
      return
    }

    this.move(event.pageX)
  }

  handleButtonKeyUp = (event: KeyboardEvent): void => {
    const button = $el(BUTTON_SELECTOR, this)

    if (!button) {
      return
    }

    if (!isLeftKey(event) && !isRightKey(event)) {
      return
    }

    const rect = button.getBoundingClientRect()
    const point = rect.right - rect.width / 2
    const position = isLeftKey(event) ? point - STEP : point + STEP

    this.move(position)
  }

  handleTouchStart = (): void => {
    this.startMove()
  }

  handleTouchEnd = (): void => {
    this.endMove()
  }

  handleTouchMove = (event: TouchEvent): void => {
    if (!this.isMoving) {
      return
    }

    event.preventDefault()

    this.move(event.targetTouches[0].clientX)
  }

  startMove(): void {
    this.isMoving = true
  }

  endMove(): void {
    this.isMoving = false
  }

  setPosition(position: number): void {
    this.style.setProperty('--gsc-drag-position', `${position}%`)
  }

  move(newPosition: number): void {
    const rect = this.getBoundingClientRect()

    if (newPosition <= rect.left) {
      this.setPosition(0)
    } else if (newPosition >= rect.right) {
      this.setPosition(100)
    } else {
      const position =
        Math.floor(((newPosition - rect.left) / this.offsetWidth) * 1000) / 10

      this.setPosition(position)
    }
  }
}
